<template>
  <li
    ref="listItem"
    :class="containerClasses"
    v-on="containerListeners"
    :data-testid="testId"
    :id="`${idPrefix}-list--${id}someth`"
    @click="($event) => $emit('selectedMenueItem')"
  >
    <i class="n-menu-item__mouse-trap" v-if="type === 'dropdown'" />
    <i :class="`n-menu-item__icon icon-${icon}`" v-if="icon" />
    <a
      v-if="type === 'external'"
      :id="`${idPrefix}--${id}`"
      rel="noopener"
      :href="link"
      class="n-menu-item__link"
      :target="newTab ? '_blank' : undefined"
      @click="handleClick"
    >
      {{ label }}
    </a>
    <NuxtLink
      v-else-if="type === 'internal'"
      :id="`${idPrefix}--${id}`"
      :to="getLink(link)"
      @click="handleClick"
      :class="{
        'n-menu-item__link': true,
        'n-menu-item__link--highlight': isHighlighted,
        'n-menu-item__link--selected': isItemHighlighted
      }"
    >
      {{ label }}
    </NuxtLink>
    <template v-else-if="type === 'dropdown'">
      <a
        :id="`${idPrefix}--${id}`"
        rel="noopener"
        href="#"
        class="n-menu-item__link n-menu-item__link--dropdown"
        @click.prevent="setDropdownVisible(null)"
      >
        {{ label }}
      </a>
      <transition name="fade">
        <ul class="n-menu-item__children" v-if="isDropdownActive" ref="dropdownChildren">
          <slot :id="`${idPrefix}-slot--${id}`" />
        </ul>
      </transition>
    </template>
    <template v-else-if="type === 'category-select'">
      <client-only>
        <!-- City selected, show category link  -->
        <NuxtLink
          :id="`${idPrefix}--${id}`"
          class="n-menu-item__link"
          v-if="categoryRoute"
          type="internal"
          :to="categoryRoute"
          @click="handleClick"
        >
          {{ categoryLabel }}
        </NuxtLink>
        <!-- City not selected, show citySelect action -->
        <a
          v-else
          :id="`${idPrefix}--${id}`"
          href="#"
          class="n-menu-item__link n-menu-item__link--dropdown"
          @click.prevent="uiStore.toggleGlobalUI('CityModal') && handleClick"
        >
          {{ $t('newsletter.placeholder_city') }}
        </a>
      </client-only>
    </template>
  </li>
</template>

<script lang="ts" setup>
import { computed, ref, onMounted, watch } from 'vue';
import { useUiStore } from '@/stores/ui';
import { removeTrailingSlash } from '@/resources/helpers';
import { onClickOutside } from '@vueuse/core';
import { useNuxtApp } from '#imports';
import { CITY_MODAL_SELECTED, MOTIV_PATH, ROOT_PATH } from '@/resources/helpers';

const BASE_CLASS = 'n-menu-item';

defineOptions({
  inheritAttrs: false
});
const emit = defineEmits(['navigating', 'dropdownClicked', 'itemClicked', 'selectedMenueItem']);
const props = defineProps({
  type: {
    type: String,
    required: true,
    validator: (val) => ['external', 'internal', 'dropdown', 'category-select'].includes(val)
  },
  label: {
    type: String,
    required: true
  },
  link: {
    type: String,
    default: null
  },
  icon: {
    type: String,
    default: null
  },
  newTab: {
    type: Boolean,
    default: false
  },
  highlight: {
    type: Boolean,
    default: false
  },
  cssFlags: {
    type: Array,
    default: () => []
  },
  children: {
    type: Array,
    default: () => []
  },
  visibleOn: {
    type: Array,
    default: () => ['xs', 'sm', 'md', 'lg']
  },
  testId: {
    type: String,
    default: ''
  },
  id: {
    type: String
  },
  idPrefix: {
    type: String,
    default: ''
  }
});

const uiStore = useUiStore();
const { $grid, $router } = useNuxtApp();

const getCurrentCity = computed(() => ({}));
const dropdownChildren = ref(null);
const isDropdownActive = ref(false);
const listItem = ref(null);
const isMobile = computed(() => $grid.md === false);

const localStorageCity = ref(null);
const isMotivPage = computed(() => props.link?.includes(MOTIV_PATH));

const isItemHighlighted = ref(false);

function highlightItem(url: string) {
  if (removeTrailingSlash(props.link) === url) {
    isItemHighlighted.value = true;
  } else {
    isItemHighlighted.value = false;
  }
}

onClickOutside(listItem, (event) => setDropdownVisible(false));

onMounted(() => {
  const url = window?.location?.pathname + window?.location?.search;

  highlightItem(url);

  if (!window.localStorage) return;

  localStorageCity.value = computed(() => localStorage.getItem(CITY_MODAL_SELECTED));
});

watch($router.currentRoute, () => {
  if (isMotivPage.value && localStorage?.getItem(CITY_MODAL_SELECTED)) {
    localStorageCity.value = computed(() => localStorage.getItem(CITY_MODAL_SELECTED));
    getLink(localStorageCity.value);
  }

  const url = window?.location?.pathname + window?.location?.search;
  highlightItem(url);
});

function getLink(link) {
  let updatedLink;

  const isTechniquenDerMarlerei = computed(() =>
    props.link?.includes('techniken-der-malerei-abstrakte-komposition-malen')
  );
  if (isMotivPage.value && localStorageCity.value?.value && !isTechniquenDerMarlerei.value) {
    updatedLink = props.link?.replace('motiv', localStorageCity.value?.value);
  } else {
    updatedLink = link;
  }
  updatedLink = removeTrailingSlash(updatedLink);
  return updatedLink;
}

const isCurrent = computed(() => {
  const isExternal = props.type === 'external';
  const isDropdown = props.type === 'dropdown';
  const isCategorySelect = props.type === 'category-select';

  if (isExternal && !props.link) return false;
  if (isDropdown) {
    const matchingChild = props.children
      .filter(({ type }) => type === 'internal')
      .find(({ link }) => link === $router.currentRoute.value.path);

    return matchingChild !== undefined;
  }
  if (isCategorySelect) {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const selectedCategory = getCurrentCity.value?.url_key;
    const isCurrentCityRoute = $router.currentRoute.value.params?.slug === selectedCategory;
    return selectedCategory && isCurrentCityRoute;
  }
  const internalRoute = props.link;

  return internalRoute === $router.currentRoute.value.fullPath;
});

const containerClasses = computed(() => [
  BASE_CLASS,
  ...props.cssFlags.map((f) => `${BASE_CLASS}--${f}`),
  isDropdownActive.value ? 'n-menu-item--dropdown-active' : '',
  isCurrent.value ? 'n-menu-item--current' : '',
  'n-menu-item--visible-on',
  ...props.visibleOn.map((f) => `n-menu-item--visible-on--${f}`)
]);

const isHighlighted = computed(() => $router.currentRoute.value.path === ROOT_PATH && props.highlight);
const isCategorySelected = computed(() => Object.keys(getCurrentCity.value || {}).length > 0);
const categoryRoute = computed(() =>
  isCategorySelected.value ? { name: 'category', params: { slug: getCurrentCity.value?.url_key } } : null
);
const categoryLabel = computed(() => props.label.replace('{{city}}', getCurrentCity.value?.name));

function setDropdownVisible(isActive: boolean) {
  if (props.type !== 'dropdown') return;
  const nextState = isActive === null ? !isDropdownActive.value : isActive;
  isDropdownActive.value = nextState;

  if (isActive === null) {
    emit('dropdownClicked');
  }
}

function handleClick(subItem = false) {
  emit('navigating');
  emit('itemClicked');
}

const noop = () => {};

// Microcart listeners are different on desktop from mobile
const containerListeners = computed(() => ({
  mouseleave: isMobile.value ? noop : () => setDropdownVisible(false)
}));
</script>

<style lang="scss" scoped>
@use '@/assets/styles/css_BASE/helpers/mixins/_bp.scss' as *;

.n-menu-item {
  list-style: none;
  font-size: 15px;
  color: var(--menu-color, black);
  position: relative;
  display: flex;
  cursor: pointer;
  z-index: 2;

  &--bold-text {
    font-weight: bold;
  }

  &--side-menu {
    flex-wrap: wrap;
  }

  &--visible-on {
    display: none;
    $breakpoints: xs, sm, md, lg;

    @each $breakpoint in $breakpoints {
      &--#{$breakpoint} {
        @include bp($breakpoint) {
          display: flex;
        }
      }
    }
  }

  &__icon {
    background-color: var(--menu-color, white);
    width: 15px;
    height: 20px;
    display: block;
    margin-right: 8px;

    .n-menu-item--side-menu & {
      position: absolute;
      top: 15px;
      right: auto;
      bottom: auto;
      left: 20px;
    }
  }

  &__mouse-trap {
    position: absolute;
    top: -40px;
    bottom: -40px;
    width: auto;
    left: -20px;
    right: -20px;
    display: none;

    @include bp(lg) {
      display: block;
    }
  }

  &__link {
    display: flex;
    align-items: center;
    border-radius: 4px;
    transition: color 300ms, background 300ms;
    width: 100%;
    position: relative;
    font-weight: 500;
    &--highlight {
      @include bp(md) {
        background: var(--color-primary);
        color: white;
        padding: 14.5px 16px;
      }
    }
    &--selected {
      color: var(--color-primary);
    }
    .n-menu-item--with-padding & {
      padding: 5px 10px;
    }
    .n-menu-item--side-menu & {
      flex-wrap: wrap;
      justify-content: space-between;
      padding: 15px 24px 24px;
      letter-spacing: 00.03em;
    }

    .n-menu-item--current .n-menu-item__children .n-menu-item--current & {
      color: var(--color-primary);
    }
    .n-menu-item--bg-hover:hover & {
      color: var(--color-primary);
    }

    .n-menu-item--dropdown &:after,
    &--dropdown:after {
      mask: url("data:image/svg+xml,%0A%3Csvg width='10' height='7' viewBox='0 0 10 7' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M5 3.93945L1.5 0.439453L0 1.93946L3.49999 5.43945L3.49997 5.43948L4.99997 6.93949L5 6.93946L5.00002 6.93947L6.50002 5.43947L6.5 5.43945L9.99999 1.93947L8.49998 0.439466L5 3.93945Z' fill='black'/%3E%3C/svg%3E%0A")
        no-repeat center;
      content: '';
      display: block;
      width: 10px;
      height: 7px;
      background: var(--color-night-blue-900);
      margin-left: 10px;
      transition: transform 300ms;

      .n-menu-item--dropdown-active & {
        transform: rotate(180deg);
      }
    }
  }

  &__children {
    --drop-down-width: 275px;
    width: 100%;
    .n-menu-item--side-menu:last-child {
      border-bottom: none;
    }
    .n-menu-item--side-menu {
      .n-menu-item__link {
        padding: 15px 20px 15px 70px;
      }
      .n-menu-item__icon {
        left: 40px;
      }
    }

    .n-menu-item:not(.n-menu-item--side-menu) & {
      position: absolute;
      display: grid;
      row-gap: 15px;
      padding: 20px;
      background: white;
      border-radius: 6px;
      width: var(--drop-down-width);
      left: 0;
      margin-top: 10px;
      filter: drop-shadow(0px 7px 5px rgba(0, 0, 0, 0.2));
      z-index: 1;
      top: 50px;
    }
  }
}
</style>
